import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import { Entry, EntrySkeletonType } from "contentful";
import { useRouter } from "next/router";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import IconChevronDown from "@/icons/icon-chevron-down";
import IconChevronUp from "@/icons/icon-chevron-up";
import Container from "~/components/common/container";
import EscapeNewLine from "~/components/common/escape-new-line";
import Grid from "~/components/common/grid";
import HighlightedText from "~/components/common/highlighted-text";
import Icon from "~/components/common/icon";
import RichText from "~/components/common/rich-text";
import Editorials from "~/types/editorials";
import accordionUtils from "~/utils/accordion-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";

import styles from "./editorial-accordion.module.scss";

type Props = {
  entry: Entry<EntrySkeletonType>;
};

export default function EditorialAccordion(props: Props) {
  const refAccordions = useRef<HTMLButtonElement[]>([]);
  const router = useRouter();
  const [path, setPath] = useState<string | undefined>(undefined);
  const hash = useMemo(() => router.asPath.split("#")[1], [router.asPath]);
  const [isFocussed, setIsFocussed] = useState(false);

  useLayoutEffect(() => {
    if (hash && refAccordions.current.length > 0 && path !== router.asPath) {
      const targetAccordion = refAccordions.current.find((accordion) => {
        return accordion.id === hash;
      });

      if (targetAccordion) {
        setTimeout(() => {
          targetAccordion.click();
        }, 300);
        setTimeout(() => {
          targetAccordion.focus({ preventScroll: true });
          window.scrollTo({
            top: targetAccordion.offsetTop - Constants.HEADER_HEIGHT,
            behavior: "smooth",
          });
        }, 600);
      }

      setPath(router.asPath);
    }
  }, [hash, path, refAccordions, router.asPath]);

  if (!isActiveEntry(props.entry)) return null;

  assertEditorialType<Editorials.EditorialAccordions>(
    props.entry,
    "editorialAccordions",
  );

  const { title, accordions, headingTag, paragraph } = props.entry.fields;
  const CustomTag = headingTag ?? "h2";

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  return (
    <Container className={styles.container}>
      <Grid>
        {title ? (
          <>
            {contentfulUtils.isHighlightText(title) ? (
              <CustomTag
                {...inspectorMode?.getProps("title")}
                className={styles.titleHighlighted}
              >
                <HighlightedText text={title} />
              </CustomTag>
            ) : (
              <CustomTag
                {...inspectorMode?.getProps("title")}
                className={styles.title}
              >
                <EscapeNewLine text={title} />
              </CustomTag>
            )}
          </>
        ) : null}
        <div
          className={classNames(
            styles.containerAccordions,
            title ? styles.marginTop : undefined,
            paragraph ? styles.marginBottom : undefined,
          )}
        >
          {accordions?.filter(isActiveEntry).map((accordion, index) => {
            const inspectorModeAccordion =
              contentfulUtils.useInspectorMode(accordion);

            return (
              <Disclosure
                as="div"
                key={accordion.sys.id}
                className={styles.accordion}
              >
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      id={accordion.fields?.anchorId}
                      ref={(ref) => {
                        if (refAccordions.current && ref)
                          refAccordions.current[index] = ref;
                      }}
                      onFocus={() =>
                        hash &&
                        hash === accordion.fields?.anchorId &&
                        setIsFocussed(true)
                      }
                      onBlur={() => setIsFocussed(false)}
                      onClick={() => {
                        setIsFocussed(false);
                        accordionUtils.handleClosingDisclosures(
                          refAccordions.current[index],
                          refAccordions,
                        );
                      }}
                      className={classNames(
                        styles.button,
                        isFocussed &&
                          hash &&
                          hash === accordion.fields?.anchorId
                          ? styles.focusButton
                          : undefined,
                      )}
                      {...inspectorModeAccordion?.getProps("title")}
                    >
                      <div className={styles.trigger}>
                        {accordion.fields.icon &&
                        isActiveEntry(accordion.fields.icon) ? (
                          <Icon
                            svgMedia={accordion.fields.icon.fields.svgMedia}
                            name={accordion.fields.icon.fields.svgIconId}
                            aria-hidden
                            width={32}
                            height={32}
                          />
                        ) : null}
                        <p
                          className={
                            contentfulUtils.isHighlightText(
                              accordion.fields.title,
                            )
                              ? styles.accordionTitleHighlighted
                              : styles.accordionTitle
                          }
                        >
                          <HighlightedText text={accordion.fields.title} />
                        </p>
                      </div>
                      <Icon
                        icon={open ? IconChevronUp : IconChevronDown}
                        aria-hidden
                        width={16}
                        height={16}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.panel}>
                      <div
                        {...inspectorModeAccordion?.getProps("content")}
                        className={styles.panelInner}
                      >
                        <RichText text={accordion.fields.content} />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            );
          })}
        </div>

        {paragraph ? (
          <div
            {...inspectorMode?.getProps("paragraph")}
            className={styles.paragraph}
          >
            <RichText text={paragraph} />
          </div>
        ) : null}
      </Grid>
    </Container>
  );
}
